import { Controller, FormProvider, useForm } from "react-hook-form";
import { FC, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";

import {
  Typography, TextField, Button, Grid,
  CircularProgress, FormHelperText,
  DialogContent, DialogActions, Alert,
  Box
} from '@mui/material';

import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppDispatch } from "@/hooks";
import { ContractDisplayTypes } from "@/components/Contracts/types/contracts";
import { RefCatastralData } from "@/components/Contracts/types/refCatastral";
import { useContracts } from "@/components/Contracts/hooks/useContracts";
import contractActions from "@/components/Contracts/actions/newActions"
import Banner from "@/components/Banner/Banner";


interface ChangeRefCatastralProps {
  type: ContractDisplayTypes;
  onClose: () => void;
}

const ChangeRefCatastral: FC<ChangeRefCatastralProps>  = ({
  type,
  onClose
}) => {

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { currentContract, refCatastralState, onUpdateRefCatastral } = useContracts(type);

  const [ actualitzar, setActualitzar ] = useState(false);

  const handleClickClose = () => {
    if (refCatastralState === "error"){
      dispatch(contractActions[type].refCatastralFormClosed());
    }
    onClose();
  };

  // Definim esquema per les validacions del form
  const schema = z.object({
    refCatastral: z.string()
      .toUpperCase()
      .min(1, t('common:text.required_field'))
      .max(20, t('common:text.ref_catastral_dialog_error_length'))
      .refine(
        refCat => checkAlphanumeric(refCat),
        {message: t('common:text.ref_catastral_dialog_error_no_alpha')}
      )
  }) satisfies z.ZodType<RefCatastralData>;

  const methods = useForm<RefCatastralData>({
    resolver: zodResolver(schema),
    defaultValues: {
      refCatastral: currentContract?.ref_catastral
    }
  });

  // Comprovem que la refCat sigui alfanumèrica
  function checkAlphanumeric(refCat: string) {
    return /^[a-z0-9]+$/i.test(refCat)
  };

  // Definim form
  const {
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const watchedRefCatastral = watch("refCatastral");

  // Comprovem si el valor de la refCat a canviar del que té el contracte
  useEffect(() => {
    if (watchedRefCatastral === ""){
      setActualitzar(false)
    }
    else{
      setActualitzar(watchedRefCatastral !== currentContract?.ref_catastral);
    }
  }, [watchedRefCatastral])

  useEffect(() => {
    if (refCatastralState === "success") {
      dispatch(contractActions[type].refCatastralFormClosed());
      handleClickClose();
    }
  }, [refCatastralState]);

  // Funció que actualitza el valor de la refCat mitjançant l'API
  const onRefCatSubmit = (formValues: RefCatastralData) => {
    onUpdateRefCatastral(formValues.refCatastral, currentContract.name);
  };

  return (
    <>
      <Grid
        container
        direction="column"
        alignItems="stretch"
        style={{ padding: 15 }}
      >
        <Typography variant="h5" sx={{ pl: 1}}>{t('common:text.ref_catastral_dialog_title')}</Typography>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onRefCatSubmit)}>
            <DialogContent>
              <Grid container alignItems="flex-start">
                <Grid item xs={12} md={12}>
                  <Box display="flex" flexDirection="column" justifyContent="flex-start" height="100%" gap={2}>
                    {refCatastralState === "error" ?
                      <>
                        <Alert severity="error" sx={{ m: 1}}>
                          {t('common:text.message_cant_do_operation')}
                        </Alert>
                      </>
          
                      :

                      <Controller
                        name="refCatastral"
                        control={control}
                        render={({ field, fieldState }) => (
                          <>
                            <TextField
                              {...field}
                              label={t('common:text.ref_catastral_dialog_hint')}
                              sx={{ width: "100%" }}
                              variant="filled"
                              minRows={2}
                              error={!!(fieldState.error)}
                            />
                            {fieldState.error && (
                              <FormHelperText error={true}>
                                {fieldState.error.message}
                              </FormHelperText>
                            )}
                          </>
                        )}
                      />
                  }
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Banner code='canvi_ref_cadastral_ov'/>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{display: "flex", justifyContent: "flex-start"}}>
              <Button
                variant={'outlined'}
                color={"primary"}
                onClick={handleClickClose}
              >
                {t('common:text.ref_catastral_dialog_btn_close')}
              </Button>
              <Button
                type="submit"
                variant={'contained'}
                color={'primary'}
              disabled={isSubmitting || refCatastralState == "submitting" || !actualitzar || refCatastralState === "error"}
              >
                {isSubmitting || refCatastralState == "submitting" ? <CircularProgress size={25} sx={{marginRight: '8px'}} /> : null}
                {t('common:text.ref_catastral_dialog_btn_update')}
              </Button>
            </DialogActions>

          </form>
        </FormProvider>
      </Grid>
    </>
  );
};

export default ChangeRefCatastral;